import { ReactNode, useContext, useEffect, useState } from 'react';
import styles from './MobileLayout.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_TITLE_MAP } from 'config/constants';
import { formatBalanceDisplay } from 'helpers/balanceFormat';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { UserContext } from 'context/userContext';
import { SlipContext } from 'context/slipContext';
import { SideMenu } from '../SideMenu/SideMenu';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import { usePlayerLayout } from 'lib/playerLayout';
import {
  ModernDrawer,
  ModernFooter,
  ModernHeader,
} from '../ModernChrome/ModernChrome';
import {
  ModernDesktopShell,
  useModernDesktop,
} from 'components/Layout/ModernDesktopShell';
import { VipFooter, VipHeader } from '../VipChrome/VipChrome';

export interface MobileLayoutProps {
  children?: ReactNode;
  title?: string | ReactNode;
  hideBack?: boolean;
  pageTitle?: string | String | undefined;
  /** Shown in the header between the page title and the menu icon (e.g. search toggle). */
  headerTrailing?: ReactNode;
  /** Renders at the top of the scrollable content, directly under the header. */
  topContent?: ReactNode;
}

export const MobileLayout = ({
  children = <></>,
  title,
  hideBack = false,
  pageTitle,
  headerTrailing,
  topContent,
}: MobileLayoutProps) => {
  const [sideMenuOpened, setSideMenuOpened] = useState(false);
  const url = useLocation().pathname;
  const { loggedUser } = useContext(UserContext);
  const { maxCoefficient, getTotalBets } = useContext(SlipContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [currentBalance, setCurrentBalance] = useState(
    loggedUser?.balance ?? 0
  );

  pageTitle = pageTitle ?? ROUTE_TITLE_MAP.get(url);

  useEffect(() => {
    setCurrentBalance(loggedUser?.balance);
  }, [loggedUser?.balance]);

  const layout = usePlayerLayout();
  const modernDesktop = useModernDesktop();
  // A phone page opened on a wide screen from the modern desktop home (a
  // match, a league, highlights): the desktop frame around the same content.
  if (modernDesktop) {
    return (
      <ModernDesktopShell>
        <div className={styles.desktopPage}>
          <div className={styles.desktopHead}>
            {!hideBack && (
              <button
                type="button"
                className={styles.desktopBack}
                onClick={() => navigate(-1)}
                aria-label={t('previous_label')}
              >
                ‹
              </button>
            )}
            <h1>{pageTitle ? t(pageTitle as string) : 'Tottalo'}</h1>
            {headerTrailing != null && (
              <div className={styles.headerTrailing}>{headerTrailing}</div>
            )}
          </div>
          {topContent}
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
      </ModernDesktopShell>
    );
  }
  if (layout === 'modern') {
    return (
      <>
        {sideMenuOpened && (
          <ModernDrawer close={() => setSideMenuOpened(false)} />
        )}
        <ModernHeader
          title={pageTitle ? t(pageTitle as string) : 'Tottalo'}
          hideBack={hideBack}
          trailing={headerTrailing}
          onMenu={() => setSideMenuOpened(true)}
        />
        <div className={classNames(styles.content, styles.modernContent)}>
          {topContent}
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
        <ModernFooter />
      </>
    );
  }
  if (layout === 'vip') {
    return (
      <>
        {sideMenuOpened && <SideMenu close={() => setSideMenuOpened(false)} />}
        <VipHeader
          title={pageTitle ? t(pageTitle as string) : 'Tottalo'}
          hideBack={hideBack}
          trailing={headerTrailing}
          onMenu={() => setSideMenuOpened(true)}
        />
        <div className={classNames(styles.content, styles.vipContent)}>
          {topContent}
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
        <VipFooter />
      </>
    );
  }

  return (
    <>
      {sideMenuOpened && <SideMenu close={() => setSideMenuOpened(false)} />}
      <div className={styles.header}>
        {!hideBack && (
          <div className={styles.headerIcon} onClick={() => navigate(-1)}>
            <ThemeMenuIcon icon="chevron-left" alt="back" variant="footer" />
          </div>
        )}
        <span className={styles.currentPage}>
          {pageTitle ? t(pageTitle as string) : 'Tottalo'}
        </span>
        {headerTrailing != null && (
          <div className={styles.headerTrailing}>{headerTrailing}</div>
        )}
        <div
          className={styles.headerIcon}
          onClick={() => setSideMenuOpened(true)}
        >
          <ThemeMenuIcon icon="other" alt="Menu" variant="footer" />
        </div>
      </div>
      <div className={styles.content}>
        {topContent}
        {title && <p className={styles.title}>{title}</p>}
        {children}
      </div>
      <div
        className={classNames(styles.footer, {
          [styles['loggedUser']]: !!loggedUser,
        })}
      >
        <span
          className={classNames(
            styles.footerItem,
            url === '/' ? styles.footerItemActive : ''
          )}
          onClick={() => navigate('/')}
        >
          <ThemeMenuIcon icon="home" alt="Home" variant="footer" />
        </span>
        {!loggedUser && (
          <span
            className={classNames(
              styles.footerItem,
              url === '/login' ? styles.footerItemActive : ''
            )}
            onClick={() => navigate('/login')}
          >
            <ThemeMenuIcon
              icon="login"
              alt={t('login_label')}
              variant="footer"
            />
            <p>{t('login_label')}</p>
          </span>
        )}
        {loggedUser && (
          <>
            <span
              className={classNames(
                styles.footerItem,
                url === '/account-settings' ? styles.footerItemActive : ''
              )}
              onClick={() => navigate('/account-settings')}
            >
              <div className={styles.valueWrapper}>
                {formatBalanceDisplay(currentBalance)}
              </div>
              <ThemeMenuIcon
                icon="account-settings"
                alt={t('account_settings_label')}
                variant="footer"
              />
              {/* One short word, so no footer label wraps (owner). */}
              <p>{t('footer_account_label')}</p>
            </span>
            <span
              className={classNames(
                styles.footerItem,
                url === '/my-bets' ? styles.footerItemActive : ''
              )}
              onClick={() => navigate('/my-bets')}
            >
              <ThemeMenuIcon
                icon="my-bets"
                alt={t('my_bets_label')}
                variant="footer"
              />
              <p>{t('my_bets_label')}</p>
            </span>
          </>
        )}
        <span
          className={classNames(
            styles.footerItem,
            url === '/sports-betting' ? styles.footerItemActive : ''
          )}
          onClick={() => navigate('/sports-betting')}
        >
          <ThemeMenuIcon
            icon="sports-betting"
            alt={t('sports_betting_label')}
            variant="footer"
          />
          <p>{t('sports_betting_label')}</p>
        </span>
        <span
          className={classNames(
            styles.footerItem,
            url === '/bet-slip' ? styles.footerItemActive : ''
          )}
          onClick={() => navigate('/bet-slip')}
        >
          <div className={styles.valueWrapper} data-color="blue">
            {maxCoefficient.toFixed(2)}
          </div>
          <div className={styles.valueWrapper} data-right={true}>
            {getTotalBets()}
          </div>
          <ThemeMenuIcon icon="slip" alt={t('slip_label')} variant="footer" />
          <p>{t('slip_label')}</p>
        </span>
      </div>
    </>
  );
};
